import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { cn, Dialog } from "@hostdock/ui";
import { api } from "../api";
import { useSession } from "../session";

interface Command {
  id: string;
  section: "Go to" | "Actions" | "Websites";
  label: string;
  hint?: string;
  keywords?: string;
  run(): void;
  /** Shown instead of running when the plan or state does not allow the action. */
  disabled?: string;
}

const norm = (s: string) => s.toLowerCase();
function score(c: Command, q: string): number {
  if (!q) return c.section === "Go to" ? 2 : c.section === "Actions" ? 1 : 0;
  const hay = norm(`${c.label} ${c.hint ?? ""} ${c.keywords ?? ""}`);
  const label = norm(c.label);
  let total = 0;
  for (const t of norm(q).split(/\s+/).filter(Boolean)) {
    if (!hay.includes(t)) return -1;
    total += label.startsWith(t) ? 3 : label.includes(t) ? 2 : 1;
  }
  return total;
}

/** Opens with Ctrl/Cmd+K. Every entry navigates somewhere real or runs a real action; plan limits disable entries with the reason. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const navigate = useNavigate();
  const { logout } = useSession();
  const sites = useQuery({ queryKey: ["websites", "palette"], queryFn: () => api.websites.list({ pageSize: 100 }), enabled: open, staleTime: 30_000 });
  const opts = useQuery({ queryKey: ["hosting-options"], queryFn: api.hosting.options, enabled: open, staleTime: 60_000 });
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => navigate(to);
    const subs = opts.data?.subscriptions ?? [];
    const canCreateSite = subs.some((s) => s.usage.websites < s.limits.websites);
    const siteBlock = !subs.length ? "No active hosting plan" : !canCreateSite ? "Website limit reached for your plan" : undefined;
    const dbBlock = subs.length && !subs.some((s) => s.limits.databases > 0) ? "Your plan does not include databases" : undefined;
    const out: Command[] = [
      { id: "g-home", section: "Go to", label: "Home", run: go("/"), keywords: "dashboard overview" },
      { id: "g-sites", section: "Go to", label: "Websites", run: go("/websites"), keywords: "sites apps" },
      { id: "g-domains", section: "Go to", label: "Domains", run: go("/domains"), keywords: "dns ssl https certificate" },
      { id: "g-db", section: "Go to", label: "Databases", run: go("/databases"), keywords: "mysql mariadb postgres mongodb" },
      { id: "g-mail", section: "Go to", label: "Email", run: go("/email"), keywords: "mailbox webmail alias forwarder dkim spf mx" },
      { id: "g-dns", section: "Go to", label: "Zone Editor (DNS)", run: go("/dns"), keywords: "dns records nameserver a cname mx txt cloudflare zone" },
      { id: "g-dev", section: "Go to", label: "Developers: API tokens and webhooks", run: go("/developers"), keywords: "api token key webhook ci automation" },
      { id: "g-mig", section: "Go to", label: "Migrate to HostDock", run: go("/migrate"), keywords: "migration move transfer cpanel plesk directadmin" },
      { id: "g-apps", section: "Go to", label: "App Installer", run: go("/apps"), keywords: "apps install joomla wordpress yourls softaculous one-click" },
      { id: "g-sec", section: "Go to", label: "Security", run: go("/security"), keywords: "password two-factor 2fa sessions" },
      { id: "g-acc", section: "Go to", label: "Account", run: go("/account"), keywords: "profile" },
      { id: "a-site", section: "Actions", label: "Create website", hint: "Deploy a new site or app", run: go("/websites/new"), disabled: siteBlock, keywords: "add new wordpress php node python" },
      { id: "a-mig", section: "Actions", label: "Migrate a website to HostDock", hint: "From cPanel, DirectAdmin, Plesk…", run: go("/migrate/new"), keywords: "migration move import transfer" },
      { id: "a-ssl", section: "Actions", label: "Check SSL certificates", hint: "Status and renewal", run: go("/domains"), keywords: "https certificate lets encrypt" },
      { id: "a-pw", section: "Actions", label: "Change password or set up two-factor", run: go("/security"), keywords: "2fa mfa security" },
      { id: "a-scan", section: "Actions", label: "Malware scans of my websites", hint: "Last result per website", run: go("/security"), keywords: "malware virus clamav scan security infected" },
      { id: "a-out", section: "Actions", label: "Sign out", run: () => void logout() },
    ];
    for (const w of sites.data?.items ?? []) {
      if (w.status === "DELETED") continue;
      const wp = w.appType === "WORDPRESS" || w.appType === "WOOCOMMERCE";
      const at = (tab: string) => go(`/websites/${w.id}?tab=${tab}`);
      const add = (id: string, label: string, tab: string, keywords = "", disabled?: string) =>
        out.push({ id: `w-${w.id}-${id}`, section: "Websites", label, hint: w.primaryDomain, run: at(tab), keywords: `${w.primaryDomain} ${keywords}`, disabled });
      out.push({ id: `w-${w.id}`, section: "Websites", label: `Open ${w.primaryDomain}`, hint: w.appType, run: go(`/websites/${w.id}`), keywords: w.primaryDomain });
      add("files", `Open file manager: ${w.primaryDomain}`, "files", "files upload edit");
      add("domains", `Add domain to ${w.primaryDomain}`, "domains", "domain dns subdomain alias redirect https ssl");
      add("db", `Create database on ${w.primaryDomain}`, "databases", "database mysql mariadb postgres phpmyadmin", dbBlock);
      add("settings", `Change PHP version or settings: ${w.primaryDomain}`, "settings", "php node python version runtime memory");
      add("cron", `Cron jobs: ${w.primaryDomain}`, "cron", "schedule task");
      add("sftp", `SFTP and SSH keys: ${w.primaryDomain}`, "sftp", "ssh key ftp password");
      add("git", `Git deployment: ${w.primaryDomain}`, "git", "deploy github");
      add("logs", `View logs: ${w.primaryDomain}`, "logs", "error access debug");
      add("scan", `Scan for malware: ${w.primaryDomain}`, "security", "malware virus clamav scan security infected");
      if (wp) {
        add("wp", `WordPress manager: ${w.primaryDomain}`, "wordpress", "wordpress plugins themes update login admin");
        add("wp-staging", `WordPress staging: ${w.primaryDomain}`, "wordpress", "wordpress staging clone");
        add("wp-backup", `Back up or restore WordPress: ${w.primaryDomain}`, "wordpress", "backup restore snapshot");
      }
    }
    return out;
  }, [navigate, logout, sites.data, opts.data]);

  const results = useMemo(() => commands.map((c) => ({ c, s: score(c, q) })).filter((x) => x.s >= 0).sort((a, b) => b.s - a.s).slice(0, 40).map((x) => x.c), [commands, q]);
  useEffect(() => setIndex(0), [q, open]);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" }); }, [index]);

  const run = (c: Command | undefined) => { if (!c || c.disabled) return; onOpenChange(false); c.run(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); run(results[index]); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Quick search" description="Jump to a page or run an action" size="md">
      <div className="-m-1 flex flex-col gap-2" onKeyDown={onKey}>
        <div className="flex items-center gap-2 rounded-md border border-line bg-surface px-3">
          <Search size={15} className="text-subtle" aria-hidden />
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls="palette-list"
            aria-activedescendant={results[index] ? `palette-${results[index]!.id}` : undefined}
            aria-label="Search pages and actions"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages, websites and actions…"
            className="h-10 flex-1 bg-transparent text-[14px] outline-none placeholder:text-subtle"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        <ul id="palette-list" ref={listRef} role="listbox" className="max-h-80 overflow-y-auto">
          {results.length === 0 && <li className="px-3 py-8 text-center text-[13px] text-muted">{sites.isPending || opts.isPending ? "Loading…" : `Nothing matches “${q}”`}</li>}
          {results.map((c, i) => (
            <li key={c.id} role="option" id={`palette-${c.id}`} aria-selected={i === index} aria-disabled={Boolean(c.disabled)}>
              <button
                type="button"
                tabIndex={-1}
                onMouseMove={() => setIndex(i)}
                onClick={() => run(c)}
                className={cn("flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-[13.5px]", i === index && "bg-accent-soft", c.disabled && "opacity-60")}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{c.label}</span>
                  {(c.disabled || c.hint) && <span className={cn("block truncate text-[12px]", c.disabled ? "text-warning" : "text-muted")}>{c.disabled ?? c.hint}</span>}
                </span>
                <span className="shrink-0 text-[11px] tracking-wide text-subtle uppercase">{c.section}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

/** Registers Ctrl/Cmd+K globally. */
export function useCommandPaletteShortcut(toggle: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);
}
